import { css } from "@emotion/css";
import { useMemo, useState, useCallback, useEffect } from "react";
import { useLocation, Navigate, matchPath } from "react-router-dom";
import { useMutation } from "@apollo/client";
import { parseQueryString } from "../../common/utils/queryString";
import { AddEmailToAccountModal } from "../../common/components/AddEmailToAccountModal";
import { useModal } from "../../core/hooks";
import type { SignEulaMutation, SignEulaMutationVariables } from "../api/mutations/signEula.gql-generated";
import { signEula } from "../api/mutations/signEula";
import { useEula } from "../hooks/useEula";
import { EulaAgreement } from "./EulaAgreement";
import { AbsoluteRoute } from "../../../core/routes";
import { PageContentSpinner } from "../../../layout/components";

export const EulaContainer = () => {
    const location = useLocation();
    const [eulaSigned, setEulaSigned] = useState<boolean>(false);
    const [firstModalOpen, setFirstModalOpen] = useState<boolean>(true);
    const { data: eulaInfoData, loading: eulaInfoLoading, error: eulaError } = useEula();
    const [modalProps, showModal] = useModal();

    const unverifiedEmailError = "email is not verified";
    const userIsUnverified = eulaError?.message.includes(unverifiedEmailError);

    useEffect(() => {
        if (firstModalOpen && userIsUnverified) {
            showModal();
            setFirstModalOpen(false);
        }
    }, [firstModalOpen, showModal, userIsUnverified]);

    const onSignEulaCompleted = (data: SignEulaMutation) => {
        if (data?.signEULA?.hasAgreedToLatestEula) {
            setEulaSigned(true);
        }
    };

    const [signEulaForUser] = useMutation<SignEulaMutation, SignEulaMutationVariables>(signEula, {
        onCompleted: onSignEulaCompleted,
    });

    const { viewonly, redirect } = useMemo(
        () => parseQueryString(location.search, ["viewonly", "redirect"]),
        [location.search],
    );

    const onAgree = useCallback(() => {
        if (!eulaInfoLoading) {
            signEulaForUser({ variables: { eulaId: eulaInfoData?.cameraKitMe?.me?.latestEulaId ?? "" } });
        }
    }, [eulaInfoLoading, signEulaForUser, eulaInfoData]);

    if ((!eulaInfoLoading && !viewonly && eulaInfoData?.cameraKitMe?.me?.hasAgreedToLatestEula) || eulaSigned) {
        const path =
            !redirect || matchPath({ path: AbsoluteRoute.CK_EULA }, redirect) ? AbsoluteRoute.CK_ROOT : redirect;
        return <Navigate to={path} />;
    }

    if (eulaInfoLoading) {
        return <PageContentSpinner />;
    }

    return (
        <>
            <AddEmailToAccountModal {...modalProps} />
            <div
                className={css({
                    minWidth: "80%",
                    maxWidth: "60ch",
                    margin: "20px auto",
                })}
            >
                <EulaAgreement
                    showButtons={Boolean(
                        eulaInfoData?.cameraKitMe && !eulaInfoData?.cameraKitMe?.me?.hasAgreedToLatestEula,
                    )}
                    onAgreeClick={onAgree}
                />
            </div>
        </>
    );
};
